import { Questionnaire } from './Questionnaire'
import * as React from 'react'
import ReactTable, { CellInfo } from 'react-table'
import LocalizationStore from '../Stores/LocalizationStore'
import PaymentsStore from '../Stores/PaymentsStore'
import { Link } from 'react-router-dom'
import HttpActionsService from '../Services/HttpActionsService'
import DateTimeHelper from '../Helpers/DateTimeHelper'
import ConfigStore from '../Stores/ConfigStore'

export enum PaymentStatus {
    New = 0,
    Pending = 1,
    Done = 2,
    Canceled = 3,
}

export enum PaymentSource {
    None = 500,
    Cash = 0,
    Virtual = 1, // Discount
    Sky = 2,
    Magnit = 3,
    Standart = 4,
    Million = 5,
    KapitalBank = 6,
    Unibank = 7,
    BankOfBaku = 8,
    EManat = 9,
    VirtualFix = 10,
    MillionManual = 11,
    MPAY = 12,
    CIB = 13
}

export enum PaymentType {
    None = 500,
    Prepayment = 1,
    MonthlyPayment = 2,
    Penalty = 3,
    Fee = 4
}

export enum PaymentDirection {
    Deposit = 1,
    Withdrawal = 2
}

export function getPaymentStatusText(paymentStatus: PaymentStatus): string {
    const ls = LocalizationStore.getInstance()
    switch (paymentStatus) {
        case PaymentStatus.New:
            return ls.t('PaymentStatusNew')
        case PaymentStatus.Pending:
            return ls.t('PaymentStatusPending')
        case PaymentStatus.Done:
            return ls.t('PaymentStatusDone')
        case PaymentStatus.Canceled:
            return ls.t('PaymentStatusCanceled')
        default:
            return paymentStatus
    }
}

export function getPaymentStatusClass(paymentStatus: PaymentStatus): string {
    switch (paymentStatus) {
        case PaymentStatus.New:
            return 'payment-new-status'
        case PaymentStatus.Pending:
            return 'payment-pending-status'
        case PaymentStatus.Done:
            return 'payment-done-status'
        case PaymentStatus.Canceled:
            return 'payment-canceled-status'
        default:
            return paymentStatus
    }
}

export function getPaymentSourceText(paymentSource: PaymentSource) {
    const ls = LocalizationStore.getInstance()
    switch (paymentSource) {
        case PaymentSource.Cash:
            return ls.t('PaymentTypeCash')
        case PaymentSource.Virtual:
            return ls.t('PaymentTypeVirtual')
        case PaymentSource.Magnit:
            return ls.t('PaymentTypeMagnit')
        case PaymentSource.Sky:
            return ls.t('PaymentTypeSky')
        case PaymentSource.Standart:
            return ls.t('PaymentTypeStandart')
        case PaymentSource.Million:
            return ls.t('PaymentTypeMillion')
        case PaymentSource.BankOfBaku:
            return ls.t('BankOfBaku')
        case PaymentSource.KapitalBank:
            return ls.t('KapitalBank')
        case PaymentSource.Unibank:
            return ls.t('Unibank')
        case PaymentSource.EManat:
            return ls.t('EManat')
        case PaymentSource.VirtualFix:
            return ls.t('VirtualFix')
        case PaymentSource.MillionManual:
            return ls.t('MillionManual')
        case PaymentSource.MPAY:
            return ls.t('PaymentTypeMPAY')
        case PaymentSource.CIB:
            return ls.t('PaymentTypeCIB')
        case PaymentSource.None:
        default:
            return ls.t('PaymentTypeNone')
    }
}

export function getPaymentTypeText(paymentType: PaymentType) {
    const ls = LocalizationStore.getInstance()
    switch (paymentType) {
        case PaymentType.MonthlyPayment:
            return ls.t('MonthlyPayment')
        case PaymentType.Penalty:
            return ls.t('Penalty')
        case PaymentType.Prepayment:
            return ls.t('Prepayment')
        case PaymentType.Fee:
            return ls.t('Fee')
        default:
            return ls.t('None')
    }
}

export function getPaymentDirectionText(paymentDirection: PaymentDirection) {
    const ls = LocalizationStore.getInstance()
    switch (paymentDirection) {
        case PaymentDirection.Deposit:
            return ls.t('Deposit')
        case PaymentDirection.Withdrawal:
            return ls.t('Withdrawal')
        default:
            return ls.t('None')
    }
}

export default class Payment {
    id: string
    createDate: string
    updateDate: string
    creditNumber: number
    paymentDateTime: string
    amount: number
    description: string
    checkLink: string
    createByUserId: string
    paymentStatus: PaymentStatus
    paymentSource: PaymentSource
    paymentType: PaymentType
    paymentDirection: PaymentDirection
    questionnaireSource: string
    questionnaireExternalKey: string
    questionnaireCreateDate: string
}

export class PaymentTableItem {
    constructor(p: Payment) {
        this.id = p.id
        this.createDate = DateTimeHelper.formatDateForList(p.createDate)
        this.updateDate = DateTimeHelper.formatDateForList(p.updateDate)
        this.creditNumber = p.creditNumber
        this.paymentDateTime = DateTimeHelper.formatDateForList(p.paymentDateTime)
        this.amount = p.amount
        this.description = p.description
        this.checkLink = p.checkLink
        this.createByUserId = p.createByUserId
        this.paymentStatus = getPaymentStatusText(p.paymentStatus)
        this.paymentSource = getPaymentSourceText(p.paymentSource)
        this.questionnaireSource = p.questionnaireSource
        this.questionnaireExternalKey = p.questionnaireExternalKey
        this.paymentType = `${p.paymentType.toString()} - ${getPaymentTypeText(p.paymentType)}`,
        this.paymentDirection = getPaymentDirectionText(p.paymentDirection)
        this.questionnaireCreateDate = DateTimeHelper.formatDateForList(p.questionnaireCreateDate)
    }

    id: string
    createDate: string
    updateDate: string
    creditNumber: number
    paymentDateTime: string
    amount: number
    description: string
    checkLink: string
    createByUserId: string
    paymentStatus: string
    paymentSource: string
    questionnaireSource: string
    questionnaireExternalKey: string
    paymentDirection: string
    paymentType: string
    questionnaireCreateDate: string
}

export class CreatePaymentRequest {
    constructor(
        questionnaireId: string,
        amount: number,
        paymentStatus: PaymentStatus,
        paymentSource: PaymentSource,
        paymentDateTime: Date,
        paymentType: PaymentType,
        paymentDirection: PaymentDirection,
        description?: string,
    ) {
        this.questionnaireId = questionnaireId
        this.amount = amount
        this.description = description
        this.paymentStatus = paymentStatus
        this.paymentSource = paymentSource,
            this.paymentDateTime = paymentDateTime,
            this.paymentType = paymentType,
            this.paymentDirection = paymentDirection
    }

    questionnaireId: string
    amount: number
    description?: string
    paymentStatus: PaymentStatus
    paymentSource: PaymentSource
    paymentDateTime: Date
    paymentType: PaymentType
    paymentDirection: PaymentDirection
}

export class UpdatePaymentStatusRequest {
    constructor(id: string, paymentStatus: PaymentStatus) {
        this.id = id
        this.paymentStatus = paymentStatus
    }

    id: string
    paymentStatus: PaymentStatus
}

export class CreatePaymentQuestionnaireModel {
    constructor(
        questionnaire: Questionnaire,
        createPaymentRequest: CreatePaymentRequest
    ) {
        this.questionnaire = questionnaire
        this.createPaymentRequest = createPaymentRequest
    }
    questionnaire: Questionnaire
    createPaymentRequest: CreatePaymentRequest
}

export function getPageCount() {
    return PaymentsStore.getInstance().getPageCount
}

export function createColumns() {
    const localizationStore = LocalizationStore.getInstance()

    return [
        {
            Header: localizationStore.t('PID'),
            maxWidth: 500,
            minWidth: 100,
            accessor: 'id',
            sortable: false,
            Cell: (cellInfo: CellInfo) => (
                <Link
                    className="pDetailLink"
                    to={'/payment/' + cellInfo.row.id}
                    id={cellInfo.row.name}
                >
                    {cellInfo.row.id}
                </Link>
            )
        },
        {
            Header: localizationStore.t('creditNumber'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'creditNumber',
            sortable: false,
        },
        {
            Header: localizationStore.t('questionnaireCreateDate'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'questionnaireCreateDate',
            sortable: false,
        },
        {
            Header: localizationStore.t('source'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'questionnaireSource',
            sortable: false,
        },
        {
            Header: localizationStore.t('externalKey'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'questionnaireExternalKey',
            sortable: false,
        },
        {
            Header: localizationStore.t('amountSum'),
            accessor: 'amount',
            maxWidth: 300,
            minWidth: 100,
            sortable: false,
            // filterMethod: (filter: any, row: any) =>
            //     -1 !== row[filter.id].props.children
            //         .toUpperCase()
            //         .indexOf(filter.value.toUpperCase())
        },
        {
            Header: localizationStore.t('paymentDateTime'),
            accessor: 'paymentDateTime',
            maxWidth: 300,
            minWidth: 100,
            sortable: false
        },
        {
            Header: localizationStore.t('date'),
            accessor: 'createDate',
            maxWidth: 300,
            minWidth: 100,
            sortable: false
        },
        {
            Header: localizationStore.t('updateDate'),
            accessor: 'updateDate',
            maxWidth: 300,
            minWidth: 100,
            sortable: false
        },
        {
            Header: localizationStore.t('paymentStatus'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'paymentStatus',
            sortable: false,
        },
        {
            Header: localizationStore.t('paymentType'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'paymentType',
            sortable: false,
        },
        {
            Header: localizationStore.t('paymentSource'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'paymentSource',
            sortable: false,
        },
        {
            Header: localizationStore.t('paymentDirection'),
            maxWidth: 300,
            minWidth: 100,
            accessor: 'paymentDirection',
            sortable: false,
        },
        {
            Header: localizationStore.t('inProgressByUserId'),
            accessor: 'createByUserId',
            Cell: (cellInfo: CellInfo) => (
                <Link
                    className="userDetailLink"
                    to={'/user/' + cellInfo.row.createByUserId}
                    id={cellInfo.row.createByUserId}
                >
                    {cellInfo.row.createByUserId}
                </Link>
            ),
            maxWidth: 400,
            minWidth: 100,
            sortable: false,
        }
    ]
}

export function createStub(payment: Array<Payment>): Array<PaymentTableItem> {
    return payment.map(x => new PaymentTableItem(x))
}

export function onFetchPaymentsData(state: any, instance: any) {
    HttpActionsService
        .getInstance()
        .getPayments(
            state.page,
            state.pageSize,
            state.filtered,
            state.sorted)
}

export function getPaymentsTable() {
    const localizationStore = LocalizationStore.getInstance()
    const paymentssStore = PaymentsStore.getInstance()
    const configStore = ConfigStore.getInstance()

    return (
        <ReactTable
            pages={getPageCount()}
            data={createStub(paymentssStore.getList)}
            columns={createColumns()}
            defaultPageSize={configStore.getPageSize}
            className="-striped -highlight"
            noDataText={localizationStore.t('No data text')}
            filterable={true}
            pageSizeOptions={[5, 10, 20, 25, 50, 100]}
            previousText={localizationStore.t('Previous')}
            nextText={localizationStore.t('Next')}
            pageText={localizationStore.t('Page')}
            ofText={localizationStore.t('of')}
            manual
            onFetchData={onFetchPaymentsData}
        />
    )
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Model/Payment.tsx